import type { ToolCallCategory } from "../../contexts/chat/type";
import type { CustomerEvidenceReference } from "../../contexts/chat/nucleus_conversation_client";

const TECHNICAL_TEXT = new RegExp(
  [
    "(?:s3|https?)://",
    "arn:aws",
    "\\b[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\\.[a-z0-9-]+)+\\b",
    "\\bx-amz-(?:algorithm|credential|date|expires|security-token|signature|signedheaders)\\b",
    "\\b(?:aws\\s+account(?:\\s+id)?|aws_account_id)\\s*(?::|=|is)?\\s*\\d{12}\\b",
    "\\b(?:accessdenied|validation|resource(?:notfound|inuse)|throttling|internalserver|serviceunavailable|unrecognizedclient|expiredtoken)exception\\b",
    "\\b(?:receipt|run|input|organization|tenant|request|turn|conversation|object)[ _-]?id\\b",
    "\\b(?:bucket|object[ _-]?key|primitive|tool(?: call)?|lambda|adapter|service|schema|sql|dynamodb|cloudformation|ecs|parquet|perspective[-_ ]?server|nucleusapi)\\b",
    "\\b(?:atomic_(?:master|pivots)|(?:sales_order|purchase_order|inventory|forecast)_master|main_(?:po_orders|sales_orders|inventory|forecast)|(?:organization|tenant|request|turn|conversation|receipt|run|input|workflow|object|source_table)_id|run_attempt_id|settings_name_and_version|(?:order_placed|order_delivery|purchase_order)_(?:date|week|month))\\b",
    "\\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\\b",
    "\\b[A-Za-z0-9_-]{32,}\\b",
    "(?:^|[\\s(])(?:[A-Za-z]:\\\\|/|(?:\\.?\\.?/)?[A-Za-z0-9_.-]+/)[^\\s)]+",
    "\\b[^\\s/\\\\]+\\.(?:csv|js|jsx|json|md|parquet|py|ts|tsx|ya?ml)\\b",
  ].join("|"),
  "i",
);

export const customerFacingActivity = (raw: string): string => {
  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/[.!]+$/u, "");
  const trustedActivities: Record<string, string> = {
    "understanding your question": "Understanding your question",
    "inspecting demand contributors": "Inspecting demand contributors",
    "comparing demand in the selected view":
      "Comparing demand in the selected view",
    "comparing the current forecast periods":
      "Comparing the current forecast periods",
    "reviewing supply drivers and balances":
      "Reviewing supply drivers and balances",
    "reviewing purchase-order priorities":
      "Reviewing purchase-order priorities",
    "reviewing purchase-order drivers": "Reviewing purchase-order drivers",
    "reviewing inventory risk in the selected view":
      "Reviewing inventory risk in the selected view",
    "reviewing inventory risk and shortages":
      "Reviewing inventory risk and shortages",
    "reviewing customer documentation": "Reviewing customer documentation",
    "inspecting the selected planning run":
      "Inspecting the selected planning run",
    "summarizing what matters": "Summarizing what matters",
  };
  if (trustedActivities[normalized]) return trustedActivities[normalized];

  if (/\b(?:cell|rendered value|selected value)\b/.test(normalized)) {
    return "Checking the selected cell";
  }
  if (
    /\b(?:lineage|trac|contribut|source row|reconcil|calculation)\w*\b/.test(
      normalized,
    )
  ) {
    return "Tracing contributing data";
  }
  if (/\b(?:document|guidance|knowledge|methodology)\w*\b/.test(normalized)) {
    return "Reviewing customer documentation";
  }
  if (
    /\b(?:demand|supply|inventory|forecast|purchase order|planning run)\b/.test(
      normalized,
    )
  ) {
    return "Checking the selected run";
  }

  if (/\b(?:summar|answer|recommend)\w*\b/.test(normalized)) {
    return "Summarizing what matters";
  }

  return "Understanding your question";
};

export const customerFacingEvidenceText = (
  value: string | null | undefined,
  fallback: string,
  maxLength = 240,
): string => {
  const normalized = value?.trim().replace(/\s+/g, " ") ?? "";
  if (
    normalized.length === 0 ||
    normalized.length > maxLength ||
    TECHNICAL_TEXT.test(normalized)
  ) {
    return fallback;
  }
  return normalized;
};

export const customerFacingEvidenceTitle = (
  value: string | null | undefined,
  fallback: string,
  maxLength = 160,
): string => {
  const safe = customerFacingEvidenceText(value, fallback, maxLength);
  if (safe === fallback) return fallback;

  const underscored = safe.trim();
  if (!/^[A-Za-z0-9]+(?:_[A-Za-z0-9]+)+$/.test(underscored)) {
    return safe;
  }

  const isPattern = /^pattern_/i.test(underscored);
  const words = underscored
    .replace(/^pattern_/i, "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.toLocaleLowerCase("en-US"));
  if (words.length === 0) return fallback;

  const humanized = words.join(" ");
  const titled = `${humanized.charAt(0).toLocaleUpperCase("en-US")}${humanized.slice(1)}`;
  return isPattern && !/\bcalculation\b/i.test(titled)
    ? `${titled} calculation`
    : titled;
};

export const evidenceFallback = (
  kind: CustomerEvidenceReference["kind"],
): string => {
  if (kind === "documentation") return "Customer documentation";
  if (kind === "lineage") return "Calculation trace";
  return "Selected run data";
};

export const toolCategoryLabel = (category: ToolCallCategory): string => {
  const labels: Record<ToolCallCategory, string> = {
    risk: "Reviewing inventory risk",
    supply: "Checking supply data",
    comparison: "Comparing contributing factors",
    history: "Reviewing historical data",
    transform: "Preparing the selected data",
  };
  return labels[category];
};
